import { useState } from "react";
import { toast } from "sonner";
import { Download, CreditCard, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Section, Empty, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate, money, downloadFile } from "@/lib/api";

async function startCheckout(body) {
  const { data } = await api.post("/billing/checkout", { ...body, origin_url: window.location.origin });
  if (data.provider === "stripe") { window.location.href = data.checkout_url; return; }
  const { load } = await import("@cashfreepayments/cashfree-js");
  const cf = await load({ mode: data.mode === "production" ? "production" : "sandbox" });
  await cf.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
}

function ProviderButtons({ prov, currency, onPay, testid }) {
  if (prov && !prov.stripe && !prov.cashfree) {
    return <Button size="sm" disabled data-testid={`${testid}-coming-soon`}><CreditCard size={13} className="mr-1" />Online payment coming soon</Button>;
  }
  return (
    <div className="flex gap-2 flex-wrap">
      <Button size="sm" onClick={() => onPay("stripe")} disabled={!prov?.stripe} data-testid={`${testid}-stripe`}><CreditCard size={13} className="mr-1" />Card (Stripe)</Button>
      {currency === "INR" && <Button size="sm" variant="outline" onClick={() => onPay("cashfree")} disabled={!prov?.cashfree} data-testid={`${testid}-cashfree`}>UPI / Cards (Cashfree)</Button>}
    </div>
  );
}

function PackageCard({ p, prov, renewing }) {
  const [code, setCode] = useState("");
  const [quote, setQuote] = useState(null);
  const check = async () => {
    try { const { data } = await api.post("/billing/coupons/validate", { code, package_id: p.id }); setQuote(data); toast.success(`Coupon applied: −${money(data.discount, data.currency)}`); }
    catch (e) { setQuote(null); toast.error(errMsg(e)); }
  };
  const pay = async (provider) => {
    try { await startCheckout({ provider, package_id: p.id, coupon_code: quote?.code || null }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel kpi-bar p-5 flex flex-col gap-3" data-testid={`package-card-${p.id}`}>
      <div className="font-display text-2xl font-bold uppercase">{p.name}</div>
      <div className="text-sm text-slate-400">{p.description || `${p.duration_days} days of coaching`}</div>
      <div className="font-display text-4xl font-bold" data-testid={`package-price-${p.id}`}>
        {quote ? <><span className="line-through text-slate-600 text-2xl mr-2">{money(p.price, p.currency)}</span>{money(quote.total, p.currency)}</> : money(p.price, p.currency)}
      </div>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="h-9" data-testid={`coupon-input-${p.id}`} />
        <Button size="sm" variant="ghost" onClick={check} disabled={!code} data-testid={`coupon-apply-${p.id}`}><Tag size={13} className="mr-1" />Apply</Button>
      </div>
      <div className="eyebrow">{renewing ? "Renew - extends your current end date" : "Start membership"}</div>
      <ProviderButtons prov={prov} currency={p.currency} onPay={pay} testid={`package-pay-${p.id}`} />
    </div>
  );
}

export default function MyBilling() {
  const [m] = useLoad("/billing/membership");
  const [packages] = useLoad("/billing/packages");
  const [invoices] = useLoad("/billing/invoices");
  const [prov] = useLoad("/billing/providers");
  const payInvoice = async (inv, provider) => {
    try { await startCheckout({ provider, invoice_id: inv.id }); } catch (e) { toast.error(errMsg(e)); }
  };
  const hasM = m && m.package_name;
  const noProviders = prov && !prov.stripe && !prov.cashfree;

  return (
    <div>
      <PageHeader eyebrow="Payments & renewals" title="Membership" testid="my-billing-title" />
      {noProviders && <div className="panel border-amber-500/40 p-4 mb-6 text-sm text-amber-300" data-testid="providers-unavailable">Online payment coming soon. Your coach can create an invoice and record your payment offline in the meantime.</div>}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Section title="Current membership" testid="my-membership">
          {hasM ? (
            <div className="space-y-2 text-sm">
              <div className="font-display text-2xl font-bold uppercase">{m.package_name}</div>
              <StatusBadge status={m.status} testid="my-membership-status" />
              <div className="text-slate-400">{fmtDate(m.start)} → {fmtDate(m.end)}</div>
            </div>
          ) : <Empty text="You don't have a membership yet." />}
        </Section>
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {!packages?.length ? <Empty text="No packages available from your coach yet." /> : packages.map((p) => <PackageCard key={p.id} p={p} prov={prov} renewing={hasM} />)}
        </div>
      </div>
      <Section title="Invoices & receipts" testid="my-invoices">
        {!invoices?.length ? <Empty text="No invoices yet." /> : invoices.map((i) => (
          <div key={i.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-t border-[#1E293B] text-sm" data-testid={`my-invoice-${i.id}`}>
            <div><span className="font-mono-k text-xs">{i.number}</span> · {i.package_name} <span className="text-slate-500 text-xs ml-1">{fmtDate(i.created_at)}</span>
              {i.installment_count > 1 && <div className="text-xs text-slate-500">Installment {i.installment_number} of {i.installment_count} · due {fmtDate(i.due_date)}{i.status === "scheduled" ? " · payment opens 3 days before" : ""}</div>}
              {i.status === "failed" && <div className="text-xs text-red-400">Payment failed: {i.failure_reason}. Please retry.</div>}</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono-k">{money(i.total, i.currency)}</span><StatusBadge status={i.status} />
              {["open", "failed"].includes(i.status) && <ProviderButtons prov={prov} currency={i.currency} onPay={(p) => payInvoice(i, p)} testid={`invoice-pay-${i.id}`} />}
              <Button variant="ghost" size="sm" onClick={() => downloadFile(`/billing/invoices/${i.id}/pdf`, `${i.number}.pdf`)} data-testid={`my-invoice-pdf-${i.id}`}>
                <Download size={13} className="mr-1" />{i.status === "paid" ? "Receipt" : "Invoice"}
              </Button>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}
